package app.nestbridge.feature.profile.presentation

import androidx.compose.runtime.Composable
import androidx.compose.runtime.getValue
import androidx.compose.runtime.mutableStateOf
import androidx.compose.runtime.remember
import androidx.compose.runtime.rememberCoroutineScope
import androidx.compose.runtime.saveable.rememberSaveable
import androidx.compose.runtime.setValue
import com.karawek.core.auth.model.OtpChannel
import com.karawek.core.auth.screen.OtpVerificationResult
import com.karawek.core.auth.screen.OtpVerificationScreen
import app.nestbridge.app.usecase.GetUserContextUseCase
import app.nestbridge.app.usecase.ReadAuthSessionUseCase
import app.nestbridge.app.usecase.SetUserContextUseCase
import app.nestbridge.domain.identity.model.UserProfile
import app.nestbridge.domain.identity.usecase.SaveUserProfileUseCase
import app.nestbridge.domain.identity.usecase.SaveUserUseCase
import app.nestbridge.feature.profile.screen.CreateUserProfileEvent
import app.nestbridge.feature.profile.screen.CreateUserProfileScreen
import app.nestbridge.feature.profile.screen.CreateUserProfileResult
import app.nestbridge.feature.profile.screen.CreateUserProfileState
import app.nestbridge.feature.profile.screen.VerificationStatus
import app.nestbridge.feature.profile.screen.canContinue
import com.slack.circuit.codegen.annotations.CircuitInject
import com.slack.circuit.foundation.rememberAnsweringNavigator
import com.slack.circuit.runtime.Navigator
import com.slack.circuit.runtime.presenter.Presenter
import kotlinx.coroutines.launch
import app.nestbridge.app.util.launchSafely
import me.tatarka.inject.annotations.Assisted
import me.tatarka.inject.annotations.Inject
import software.amazon.lastmile.kotlin.inject.anvil.AppScope
import kotlin.time.Clock.System

@CircuitInject(
    screen = CreateUserProfileScreen::class,
    scope = AppScope::class,
)
@Inject
class CreateUserProfilePresenter(
    @Assisted private val screen: CreateUserProfileScreen,
    @Assisted private val navigator: Navigator,
    private val readAuthSessionUseCase: ReadAuthSessionUseCase,
    private val getUserContextUseCase: GetUserContextUseCase,
    private val setUserContextUseCase: SetUserContextUseCase,
    private val saveUserUseCase: SaveUserUseCase,
    private val saveUserProfileUseCase: SaveUserProfileUseCase,
) : Presenter<CreateUserProfileState> {

    @Composable
    override fun present(): CreateUserProfileState {
        val scope = rememberCoroutineScope()
        val authInfo = screen.createUserProfileUserInfo
        val initialFullName = screen.prefillName?.takeIf { it.isNotBlank() }
            ?: buildFullName(
                first = authInfo?.firstName,
                middle = authInfo?.middleName,
                last = authInfo?.lastName,
            )
        val primaryEmail = authInfo?.emailAddresses?.firstOrNull()
        val primaryPhone = authInfo?.phoneNumbers?.firstOrNull()
        val relayEmail = when {
            !screen.relayEmail.isNullOrBlank() -> screen.relayEmail
            isAppleRelayEmail(primaryEmail) -> primaryEmail
            else -> null
        }
        val initialEmailAddress = if (relayEmail != null && relayEmail == primaryEmail) {
            ""
        } else {
            primaryEmail.orEmpty()
        }

        var fullName by rememberSaveable { mutableStateOf(initialFullName.orEmpty()) }
        var phoneNumber by rememberSaveable { mutableStateOf(primaryPhone.orEmpty()) }
        var emailAddress by rememberSaveable { mutableStateOf(initialEmailAddress) }
        var phoneStatus by rememberSaveable {
            mutableStateOf(
                if (primaryPhone.isNullOrBlank()) {
                    VerificationStatus.Unverified
                } else {
                    VerificationStatus.Verified
                }
            )
        }
        var emailStatus by rememberSaveable {
            mutableStateOf(
                if (initialEmailAddress.isBlank()) {
                    VerificationStatus.Unverified
                } else {
                    VerificationStatus.Verified
                }
            )
        }
        var isSaving by rememberSaveable { mutableStateOf(false) }
        var errorMessage by rememberSaveable { mutableStateOf<String?>(null) }

        suspend fun saveProfile() {
            // Get auth session for identityId
            val session = readAuthSessionUseCase() ?: throw IllegalStateException("No auth session found")

            // Build emails list (deduplicated) - only include verified emails
            val emails = buildList {
                if (emailStatus == VerificationStatus.Verified && emailAddress.isNotBlank()) {
                    add(emailAddress)
                }
                relayEmail?.takeIf { it.isNotBlank() }?.let { add(it) }
            }.distinct()

            // Build phone numbers list - only include verified phone
            val phoneNumbers = if (phoneStatus == VerificationStatus.Verified && phoneNumber.isNotBlank()) {
                listOf(phoneNumber)
            } else {
                emptyList()
            }

            // Get current context
            val currentContext = requireNotNull(getUserContextUseCase().value) {
                "User profile creation requires a UserContext"
            }
            val now = System.now()

            // Update user with verified contacts
            val updatedUser = saveUserUseCase(
                currentContext.user.copy(
                emails = emails,
                phoneNumbers = phoneNumbers,
                modifiedAt = now,
                modifiedBy = session.userId,
                ),
            )

            // Create user profile
            val userProfile = UserProfile(
                id = "",
                userId = currentContext.user.id,
                fullName = fullName,
                displayName = fullName,
                profilePictureUrl = null,
                locale = "en-US",
                timezone = "UTC",
                createdAt = now,
                createdBy = session.userId,
                modifiedAt = now,
                modifiedBy = session.userId,
            )

            // Save profile
            val savedUserProfile = saveUserProfileUseCase(userProfile)

            setUserContextUseCase(currentContext.copy(user = updatedUser, userProfile = savedUserProfile))
        }

        var pendingPhoneVerification by rememberSaveable { mutableStateOf<String?>(null) }
        var pendingEmailVerification by rememberSaveable { mutableStateOf<String?>(null) }

        val phoneOtpNavigator = rememberAnsweringNavigator<OtpVerificationResult>(navigator) { result ->
            when (result.status) {
                OtpVerificationResult.Status.Verified -> {
                    pendingPhoneVerification?.let { phone ->
                        phoneNumber = phone
                        phoneStatus = VerificationStatus.Verified
                    }
                    pendingPhoneVerification = null
                }
                OtpVerificationResult.Status.Cancelled -> {
                    pendingPhoneVerification = null
                }
            }
        }

        val emailOtpNavigator = rememberAnsweringNavigator<OtpVerificationResult>(navigator) { result ->
            when (result.status) {
                OtpVerificationResult.Status.Verified -> {
                    pendingEmailVerification?.let { email ->
                        emailAddress = email
                        emailStatus = VerificationStatus.Verified
                    }
                    pendingEmailVerification = null
                }
                OtpVerificationResult.Status.Cancelled -> {
                    pendingEmailVerification = null
                }
            }
        }

        fun onEvent(
            event: CreateUserProfileEvent,
        ) {
            when (event) {
                CreateUserProfileEvent.OnAppear -> Unit

                is CreateUserProfileEvent.UpdateFullName -> {
                    fullName = event.value
                    errorMessage = null
                }

                is CreateUserProfileEvent.UpdatePhoneNumber -> {
                    phoneNumber = event.value
                    errorMessage = null
                }

                is CreateUserProfileEvent.UpdateEmailAddress -> {
                    emailAddress = event.value
                    errorMessage = null
                }

                CreateUserProfileEvent.VerifyPhone -> {
                    val phoneToVerify = phoneNumber
                    pendingPhoneVerification = phoneToVerify
                    phoneOtpNavigator.goTo(
                        OtpVerificationScreen(
                            channel = OtpChannel.Phone,
                            destination = phoneToVerify,
                            loginOrCreate = false,
                        ),
                    )
                }

                CreateUserProfileEvent.VerifyEmail -> {
                    val emailToVerify = emailAddress
                    pendingEmailVerification = emailToVerify
                    emailOtpNavigator.goTo(
                        OtpVerificationScreen(
                            channel = OtpChannel.Email,
                            destination = emailToVerify,
                            loginOrCreate = false,
                        ),
                    )
                }

                CreateUserProfileEvent.Continue -> {
                    if (fullName.trim().isEmpty()) {
                        errorMessage = "Please enter your name to continue."
                        return
                    }

                    scope.launchSafely(onError = {
                        isSaving = false
                        errorMessage = "We couldn't save your profile. Please try again."
                    }) {
                        isSaving = true
                        errorMessage = null
                        saveProfile()
                        navigator.pop(
                            CreateUserProfileResult(
                                status = CreateUserProfileResult.Status.Completed,
                            ),
                        )
                        isSaving = false
                    }
                }

                CreateUserProfileEvent.DismissError -> {
                    errorMessage = null
                }
            }
        }

        return CreateUserProfileState(
            fullName = fullName,
            phoneNumber = phoneNumber,
            emailAddress = emailAddress,
            relayEmail = relayEmail,
            phoneStatus = phoneStatus,
            emailStatus = emailStatus,
            isSaving = isSaving,
            errorMessage = errorMessage,
            eventSink = ::onEvent,
        )
    }
}

private fun buildFullName(
    first: String?,
    middle: String?,
    last: String?,
): String? {
    val parts = listOfNotNull(
        first?.takeIf { it.isNotBlank() },
        middle?.takeIf { it.isNotBlank() },
        last?.takeIf { it.isNotBlank() },
    )
    return parts.takeIf { it.isNotEmpty() }?.joinToString(" ")
}

private fun isAppleRelayEmail(
    email: String?,
): Boolean {
    val domain = email?.substringAfterLast('@', missingDelimiterValue = "")
    return domain.equals("privaterelay.appleid.com", ignoreCase = true)
}
